import React, { useEffect, useRef } from 'react'

import amuseBoostModalBg from '@assets/amuse-boost/expand-element.svg'

import gsap from 'gsap'
import { useConfigurationStore } from '@stores'
import { DoubleChanceButton } from '../'
import BuyBonusButton from '../BuyBonus/BuyBonusButton'
import { extractScale } from '@utils/misc'
import { cn } from '@amusnet/react-ui'
import { useAmuseBoostPromoWidgetPositions } from '@hooks/useAmuseBoostPromoWidgetPositions'

interface AmuseBoostModalProps {
    isOpen: boolean
    onClose: () => void
    hasBuyBonus: boolean
    hasDoubleChance: boolean
    onSelectBuyBonus: () => void
}

const AmuseBoostModal: React.FC<AmuseBoostModalProps> = ({
    isOpen,
    onClose,
    hasBuyBonus,
    hasDoubleChance,
    onSelectBuyBonus,
}) => {
    const amuseBoostModalRef = useRef<HTMLDivElement>(null)

    const styles = useConfigurationStore((s) => s.getComponentConfig('AmuseBoostModal').styles)
    const dimensions = useConfigurationStore((s) => s.getComponentConfig('AmuseBoostModal').dimensions)
    const configPosition = useConfigurationStore((s) => s.getComponentConfig('AmuseBoostModal').position)
    const viewport = useConfigurationStore((s) => s.viewport)
    const scale = extractScale(styles ?? '') || 1

    const { secondaryButtonPosition } = useAmuseBoostPromoWidgetPositions()
    const xValue = parseInt(secondaryButtonPosition?.left ?? secondaryButtonPosition?.right ?? '1', 10) * scale

    const position = {
        bottom: configPosition?.bottom,
        top: configPosition?.top,
        left: secondaryButtonPosition?.left ? xValue : undefined,
        right: secondaryButtonPosition?.right ? xValue : undefined,
    }

    useEffect(() => {
        if (!isOpen) return

        const el = amuseBoostModalRef.current

        if (!el) return

        gsap.killTweensOf(el)

        gsap.fromTo(
            el,
            {
                scale: 0.1,
                opacity: 0,
                y: 300,
            },
            {
                scale,
                opacity: 1,
                y: 0,
                duration: 0.48,
                ease: 'back.out(0.9)',
            }
        )
    }, [isOpen, viewport])

    if (!isOpen) return null

    return (
        <>
            <div className="fixed inset-0 bg-black" style={{ zIndex: 202, opacity: 0.6 }} onClick={onClose} />

            <div
                className={cn('fixed', styles)}
                ref={amuseBoostModalRef}
                style={{
                    ...dimensions,
                    ...position,
                    zIndex: 202,
                }}
            >
                <div className="relative w-full h-full">
                    <img src={amuseBoostModalBg} alt="" className="absolute inset-0 w-full h-full" draggable={false} />

                    {hasBuyBonus && <BuyBonusButton onClick={onSelectBuyBonus} isCompact={true} />}

                    {hasDoubleChance && <DoubleChanceButton isCompact={true} />}
                </div>
            </div>
        </>
    )
}

export default AmuseBoostModal
